import React, { useCallback, useMemo, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight } from 'lucide-react';
import { NsKey } from 'enums/NsKey';
import { useAbp } from 'core/hooks/useAbp';
import { MenuItem } from 'features/dashboard/dashboard-menu/DashboardMenuItem';
import { DashboardMenuLogout } from 'features/dashboard/dashboard-menu/DashboardMenuLogout';
import { BoDashboardMenuLogo } from './BoDashboardMenuLogo';
import { BoMenuGroup, boMenuGroups } from './boMenuItems';

interface DashboardMenuProps {
  isCollapsed: boolean
  onToggle: () => void
}

const FLYOUT_CLOSE_DELAY_MS = 150;

/**
 * Menu BO desktop : groupes logiques + flyout au survol sur la droite (fini la longue
 * liste scrollable). Le flyout est en position fixed pour ne pas être rogné par le
 * scroll de la sidebar.
 */
export const BoDashboardMenu: React.FC<DashboardMenuProps> = ({ isCollapsed, onToggle }) => {
  const { t } = useTranslation(NsKey.Common);
  const { hasPermission } = useAbp();
  const location = useLocation();

  const [openGroupId, setOpenGroupId] = useState<string | null>(null);
  const [flyoutTop, setFlyoutTop] = useState(0);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Filtrage par permissions : entrées puis groupes vides
  const visibleGroups = useMemo(() => {
    return boMenuGroups
      .filter((group) => !group.permission || hasPermission(group.permission))
      .map((group) => ({
        ...group,
        items: group.items?.filter(
          (item) => !item.permission || hasPermission(item.permission)
        )
      }))
      .filter((group) => group.to || (group.items && group.items.length > 0));
  }, [hasPermission]);

  const isItemActive = useCallback(
    (to?: string) =>
      !!to && (location.pathname === to || location.pathname.startsWith(to + '/')),
    [location.pathname]
  );
  // Match le plus spécifique parmi une liste (évite que /prelevements ET /prelevements/hbs
  // soient tous deux surlignés — seul le plus long `to` qui matche gagne)
  const mostSpecificActiveId = useCallback(
    (items: MenuItem[]): string | null => {
      let bestId: string | null = null;
      let bestLen = -1;
      for (const item of items) {
        if (isItemActive(item.to) && (item.to?.length ?? 0) > bestLen) {
          bestLen = item.to?.length ?? 0;
          bestId = item.id;
        }
      }
      return bestId;
    },
    [isItemActive]
  );
  const isGroupActive = useCallback(
    (group: BoMenuGroup) =>
      isItemActive(group.to) || !!group.items?.some((item) => isItemActive(item.to)),
    [isItemActive]
  );

  const cancelClose = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimerRef.current = setTimeout(() => setOpenGroupId(null), FLYOUT_CLOSE_DELAY_MS);
  };

  const openFlyout = (group: BoMenuGroup, e: React.MouseEvent<HTMLElement>) => {
    cancelClose();
    if (!group.items) {
      setOpenGroupId(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    // Garde le panneau dans le viewport (~56px par entrée + en-tête)
    const estimatedHeight = 64 + group.items.length * 44;
    setFlyoutTop(Math.max(8, Math.min(rect.top, window.innerHeight - estimatedHeight - 8)));
    setOpenGroupId(group.id);
  };

  const openGroup = visibleGroups.find((g) => g.id === openGroupId);
  const sidebarWidth = isCollapsed ? 'w-24' : 'w-56';
  const flyoutLeft = isCollapsed ? 96 : 224; // = w-24 / w-56

  const groupBaseClass =
    'flex w-full items-center rounded-xl p-3 transition-all duration-200 text-sm font-medium tracking-wide';
  const groupActiveClass = 'bg-white/20 text-white';
  const groupIdleClass = 'text-white/70 hover:bg-white/10 hover:text-white';

  const renderGroupContent = (group: BoMenuGroup) => {
    const Icon = group.icon;
    return (
      <>
        <Icon className="h-5 w-5 shrink-0" />
        {!isCollapsed && (
          <>
            <span className="ml-3 flex-1 truncate text-left">{t(group.label)}</span>
            {group.items && <ChevronRight className="h-4 w-4 opacity-60" />}
          </>
        )}
      </>
    );
  };

  return (
    <div
      className={`hidden z-30 md:flex md:flex-col md:fixed md:inset-y-0 flex-1 flex-col min-h-0 relative bg-primary ${sidebarWidth}`}
      onMouseLeave={scheduleClose}
    >
      <BoDashboardMenuLogo isCollapsed={isCollapsed} onToggle={onToggle} />
      <div className="h-24" />
      <nav className="mt-16 flex-1 space-y-1.5 px-4">
        {visibleGroups.map((group) =>
          group.to ? (
            <NavLink
              key={group.id}
              to={group.to}
              onMouseEnter={(e) => openFlyout(group, e)}
              className={`${groupBaseClass} ${isCollapsed ? 'justify-center' : ''} ${
                isGroupActive(group) ? groupActiveClass : groupIdleClass
              }`}
            >
              {renderGroupContent(group)}
            </NavLink>
          ) : (
            <button
              key={group.id}
              type="button"
              onMouseEnter={(e) => openFlyout(group, e)}
              onClick={(e) => openFlyout(group, e)}
              className={`${groupBaseClass} ${isCollapsed ? 'justify-center' : ''} ${
                isGroupActive(group) || openGroupId === group.id
                  ? groupActiveClass
                  : groupIdleClass
              }`}
            >
              {renderGroupContent(group)}
            </button>
          )
        )}
      </nav>
      <DashboardMenuLogout isCollapsed={isCollapsed} />

      {/* Flyout du groupe survolé */}
      {openGroup?.items && (
        <div
          className="fixed z-50"
          style={{ left: flyoutLeft, top: flyoutTop }}
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
        >
          {/* Pont invisible pour traverser l'espace sidebar -> panneau sans fermer */}
          <div className="absolute -left-3 top-0 h-full w-3" />
          <div className="ml-1 w-64 overflow-hidden rounded-2xl border border-primary/10 bg-white shadow-2xl">
            <p className="bg-primary/5 px-4 py-2.5 text-[11px] font-bold uppercase tracking-[0.14em] text-primary/60">
              {t(openGroup.label)}
            </p>
            <div className="p-1.5">
              {(() => {
                const activeItemId = mostSpecificActiveId(openGroup.items);
                return openGroup.items.map((item: MenuItem) => {
                  const ItemIcon = item.icon;
                  const active = item.id === activeItemId;
                  return (
                  <NavLink
                    key={item.id}
                    to={item.to!}
                    onClick={() => setOpenGroupId(null)}
                    className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors ${
                      active
                        ? 'bg-primary/10 font-semibold text-primary'
                        : 'text-gray-700 hover:bg-primary/5 hover:text-primary'
                    }`}
                  >
                    <ItemIcon className={`h-4 w-4 ${active ? 'text-primary' : 'text-gray-400'}`} />
                    {t(item.label)}
                  </NavLink>
                  );
                });
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
